/**
 * Request Detail Page — Full detail view with timeline and signature footer
 * Route: /wrms/request/:type/:id
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { Modal, Form } from 'react-bootstrap';
import { FaArrowLeft, FaCheck, FaTimes, FaHistory, FaBan, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaQuestionCircle, FaClock, FaEdit } from 'react-icons/fa';
import Sidebar from '../../components/common/Sidebar';
import Navbar from '../../components/common/Navbar';
import WRMSFormHeader from '../components/shared/WRMSFormHeader';
import ApprovalTimeline from '../components/shared/ApprovalTimeline';
import SignatureFooter from '../components/shared/SignatureFooter';
import StatusBadge from '../components/shared/StatusBadge';
import { getRequestById, getApprovalLogs, approveStage, rejectRequest, putOnHoldRequest, cancelRequest, getRequestTypeLabel, isRequestCancellableByDate, isApprovedRequestActionableByFromDate, updateGatePassTimeIn, isGatePassTimeInEditableByDate } from '../services/wrmsService';
import { getApprovalChain, normalizeApprovalChain, isCurrentApprover, getStageLabelForRole } from '../utils/ApprovalRouter';
import '../css/wrms.css';

const formatDate = (ts) => {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const FieldRow = ({ label, value }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#64748b', marginBottom: '4px' }}>
      {label}
    </div>
    <div style={{ fontSize: '0.9rem', color: '#0f172a', fontWeight: 500, background: '#f8fafc', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
      {value || '—'}
    </div>
  </div>
);

const RequestDetailPage = () => {
  const { type, id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectModal, setRejectModal] = useState(false);
  const [holdModal, setHoldModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [timeInModal, setTimeInModal] = useState(false);
  const [timeInInput, setTimeInInput] = useState('');
  const [updatingTimeIn, setUpdatingTimeIn] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [holdRemarks, setHoldRemarks] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [req, reqLogs] = await Promise.all([
          getRequestById(type, id),
          getApprovalLogs(id),
        ]);
        setRequest(req);
        setLogs(reqLogs);
      } catch (err) {
        toast.error('Failed to load request details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [type, id]);

  const chain = request
    ? (normalizeApprovalChain(request) || getApprovalChain(request.requestType, request.deptGroup, Boolean(request.handoverTo)))
    : [];

  const userRoleLower = (user?.role || '').toLowerCase();
  const isElevatedRole = ['admin', 'hr', 'ceo', 'founder'].includes(userRoleLower);

  const canApprove = request && isCurrentApprover(
    request.requestType, request.deptGroup, request.currentStage, user?.role, user?.department, request.department, user, request
  ) && (request.status === 'pending' || request.status === 'on_hold');

  const canOverride = isElevatedRole;

  // Strict ownership & date check: ONLY the employee who applied can cancel on or before the leave date
  const currentUserId = user?.uid || user?.id;
  const isOwner = request && Boolean(currentUserId && request.employeeId === currentUserId);
  const isDateValid = isRequestCancellableByDate(request);
  const canCancel = isOwner && (request?.status === 'pending' || request?.status === 'approved') && isDateValid;
  const isPastDate = isOwner && (request?.status === 'pending' || request?.status === 'approved') && !isDateValid;
  const canActionApproved = isApprovedRequestActionableByFromDate(request);

  // Gate Pass specific permission: Only applicant (or Admin/HR) can update Time In on or before the gate pass date
  const canEditGatePassTimeIn = request &&
    request.requestType === 'gate_pass' &&
    (isOwner || isElevatedRole) &&
    request.status !== 'cancelled' &&
    request.status !== 'rejected' &&
    isGatePassTimeInEditableByDate(request);

  const isGatePassDateExpired = request &&
    request.requestType === 'gate_pass' &&
    !isGatePassTimeInEditableByDate(request);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      const isHandover = request?.currentStage === 'handover';
      await approveStage(type, id, user, isHandover ? 'Handover responsibility accepted' : 'Approved');
      toast.success(isHandover ? 'Handover accepted & approved!' : 'Request approved!');
      const [updatedReq, updatedLogs] = await Promise.all([
        getRequestById(type, id),
        getApprovalLogs(id),
      ]);
      setRequest(updatedReq);
      setLogs(updatedLogs);
    } catch (err) {
      toast.error('Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!remarks.trim()) { toast.error('Remarks are required'); return; }
    setProcessing(true);
    try {
      await rejectRequest(type, id, user, remarks);
      toast.success(request?.status === 'approved' ? 'Approved request rejected and balance restored' : 'Request rejected');
      setRejectModal(false);
      setRemarks('');
      const [updatedReq, updatedLogs] = await Promise.all([
        getRequestById(type, id),
        getApprovalLogs(id),
      ]);
      setRequest(updatedReq);
      setLogs(updatedLogs);
    } catch (err) {
      toast.error(err.message || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const handleHoldConfirm = async () => {
    if (!holdRemarks.trim()) { toast.error('Remarks are required to put request on hold'); return; }
    setProcessing(true);
    try {
      await putOnHoldRequest(type, id, user, holdRemarks);
      toast.warning('Request put on hold');
      setHoldModal(false);
      setHoldRemarks('');
      const [updatedReq, updatedLogs] = await Promise.all([
        getRequestById(type, id),
        getApprovalLogs(id),
      ]);
      setRequest(updatedReq);
      setLogs(updatedLogs);
    } catch (err) {
      toast.error(err.message || 'Failed to put request on hold');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelConfirm = async () => {
    setProcessing(true);
    try {
      await cancelRequest(type, id, user, cancelReason);
      toast.success('Request cancelled successfully');
      setCancelModal(false);
      setCancelReason('');
      // Reload request & logs
      const [updatedReq, updatedLogs] = await Promise.all([
        getRequestById(type, id),
        getApprovalLogs(id),
      ]);
      setRequest(updatedReq);
      setLogs(updatedLogs);
    } catch (err) {
      toast.error(err.message || 'Failed to cancel request');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveTimeIn = async () => {
    if (!timeInInput || !timeInInput.trim()) {
      toast.error('Please enter a valid Time In');
      return;
    }
    setUpdatingTimeIn(true);
    try {
      await updateGatePassTimeIn(id, timeInInput.trim(), user);
      toast.success('Gate pass Time In updated successfully!');
      setTimeInModal(false);
      const [updatedReq, updatedLogs] = await Promise.all([
        getRequestById(type, id),
        getApprovalLogs(id),
      ]);
      setRequest(updatedReq);
      setLogs(updatedLogs);
    } catch (err) {
      toast.error(err.message || 'Failed to update Time In');
    } finally {
      setUpdatingTimeIn(false);
    }
  };

  // Build signature signatories dynamically from the approval chain
  const buildSignatories = () => {
    if (!request) return [];
    
    const chainSteps = chain && chain.length > 0 ? chain : (normalizeApprovalChain(request) || []);
    
    return chainSteps.map(step => {
      const blockRole = step.role;
      const title = blockRole === 'employee' ? 'Signature of Applicant' : `Signature of ${getStageLabelForRole(blockRole)}`;
      
      if (blockRole === 'employee') {
        const submitLog = logs.find(l => l.action === 'submitted');
        return {
          title,
          name: submitLog?.approverName || request.employeeName,
          date: submitLog?.actedAt || request.submittedAt,
        };
      }

      // 1. Direct match by log stage or approverRole
      let log = logs.find(l =>
        (l.stage === blockRole || l.approverRole === blockRole || l.stage === step.stage) &&
        ['approved', 'overridden', 'manual_add', 'handover_accepted'].includes(l.action)
      );

      // 2. Sequential fallback if not found directly
      if (!log) {
        const approvalSteps = chainSteps.filter(s => s.stage !== 'submitted');
        const actionLogs = logs.filter(l => ['approved', 'overridden', 'manual_add', 'handover_accepted'].includes(l.action));
        const stepIdx = approvalSteps.findIndex(s => s.role === blockRole || s.stage === blockRole || s.stage === step.stage);
        if (stepIdx !== -1 && actionLogs[stepIdx]) {
          log = actionLogs[stepIdx];
        }
      }

      // 3. Overall fallback: if request is approved and there is an override/manual_add log
      if (!log && request.status === 'approved') {
        const overrideLog = logs.find(l => ['overridden', 'manual_add'].includes(l.action));
        if (overrideLog) {
          log = overrideLog;
        }
      }

      return {
        title,
        name: log?.approverName || (step.stage === 'handover' && request.status === 'pending' ? `${request.handoverTo} (Pending)` : null),
        date: log?.actedAt || null
      };
    });
  };

  const renderFormFields = () => {
    if (!request) return null;
    const t = request.requestType;

    if (t === 'leave') return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        <FieldRow label="From Date" value={formatDate(request.fromDate)} />
        <FieldRow label="To Date" value={formatDate(request.toDate)} />
        <FieldRow label="No. of Days" value={`${request.daysCount} ${request.daysCount === 1 ? 'day' : 'days'}`} />
        <FieldRow
          label="Duration Type"
          value={
            request.durationLabel ||
            (request.durationType === 'half'
              ? `Half Day (${request.halfDaySession === 'second_half' ? 'Second Half (Afternoon)' : 'First Half (Morning)'})`
              : request.durationType === 'first_day_half'
              ? 'First Day Half Day'
              : request.durationType === 'last_day_half'
              ? 'Last Day Half Day'
              : request.durationType === 'both_half'
              ? 'First & Last Day Half'
              : request.durationType === 'custom'
              ? `Custom Duration (${request.daysCount} days)`
              : 'Full Day')
          }
        />
        {request.halfDaySession && request.durationType === 'half' && (
          <FieldRow
            label="Half Day Session"
            value={request.halfDaySession === 'second_half' ? 'Second Half (Afternoon - 2 PM to 6:30 PM)' : 'First Half (Morning - 10 AM to 2 PM)'}
          />
        )}
        <FieldRow label="Leave Type" value={request.leaveType?.replace(/_/g, ' ')} />
        <FieldRow label="Application Date" value={formatDate(request.applicationDate)} />
        <div style={{ gridColumn: '1/-1' }}>
          <FieldRow label="Reason for Leave" value={request.reason} />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <FieldRow label="Current Responsibility (Priority-wise)" value={request.currentResponsibility} />
        </div>
        <FieldRow label="Hand Over To" value={request.handoverTo} />
        <FieldRow label="Application Approved On" value={formatDate(request.applicationApprovedOn) || 'Pending'} />
      </div>
    );

    if (t === 'early_leave') return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        <FieldRow label="From Date" value={formatDate(request.fromDate)} />
        <FieldRow label="Duration" value={request.earlyLeaveDuration} />
        <FieldRow label="Requested Leave Time" value={request.requestedLeaveTime} />
        <FieldRow label="Application Date" value={formatDate(request.applicationDate)} />
        <div style={{ gridColumn: '1/-1' }}>
          <FieldRow label="Reason" value={request.reason} />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <FieldRow label="Current Responsibility" value={request.currentResponsibility} />
        </div>
        <FieldRow label="Hand Over To" value={request.handoverTo} />
        <FieldRow label="Application Approved On" value={formatDate(request.applicationApprovedOn) || 'Pending'} />
      </div>
    );

    if (t === 'gate_pass') {
      const timeInLog = logs.find(l => l.action === 'time_in_updated');
      const isTimeInEdited = Boolean(request.isTimeInEdited || request.timeInUpdatedAt || timeInLog);
      const editorName = request.timeInUpdatedByName || timeInLog?.approverName || (request.timeInUpdatedBy === request.employeeId ? request.employeeName : 'Employee');
      const editedAtTs = request.timeInUpdatedAt || timeInLog?.actedAt;

      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
          <FieldRow label="Date" value={formatDate(request.date)} />
          <FieldRow label="Return Date" value={formatDate(request.returnDate)} />
          <FieldRow label="Half Day" value={request.isHalfDay ? 'Yes' : 'No'} />
          <FieldRow label="Time Out" value={request.timeOut} />
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#64748b' }}>
                Time In / Return Time
              </div>
              {canEditGatePassTimeIn && (
                <button
                  type="button"
                  onClick={() => {
                    setTimeInInput(request.expectedTimeIn || request.timeIn || '');
                    setTimeInModal(true);
                  }}
                  style={{
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '6px',
                    color: '#2563eb',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    padding: '2px 8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <FaEdit size={10} /> Edit Time In
                </button>
              )}
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: '#0f172a',
              fontWeight: 500,
              background: isTimeInEdited ? '#fffdf5' : '#f8fafc',
              padding: '10px 14px',
              borderRadius: '8px',
              border: isTimeInEdited ? '1px solid #fde68a' : '1px solid #e2e8f0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>
                    {request.expectedTimeIn || request.timeIn || '—'}
                  </span>
                  {isTimeInEdited && (
                    <span style={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: '#b45309',
                      background: '#fef3c7',
                      border: '1px solid #fde68a',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      ✏️ Edited
                    </span>
                  )}
                </div>
                {!canEditGatePassTimeIn && isGatePassDateExpired && (
                  <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                    Date passed (Locked)
                  </span>
                )}
              </div>

              {/* Who edited it and when */}
              {isTimeInEdited && (
                <div style={{
                  marginTop: '8px',
                  paddingTop: '6px',
                  borderTop: '1px dashed #fde68a',
                  fontSize: '0.74rem',
                  color: '#475569',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#64748b' }}>Edited by:</span>
                    <strong style={{ color: '#0f172a' }}>{editorName}</strong>
                    {request.timeInUpdatedByRole && (
                      <span style={{
                        fontSize: '0.65rem',
                        color: '#4338ca',
                        background: '#e0e7ff',
                        padding: '1px 6px',
                        borderRadius: '4px',
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}>
                        {request.timeInUpdatedByRole.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{formatDateTime(editedAtTs)}</span>
                    {request.previousTimeIn && (
                      <span style={{ color: '#94a3b8' }}>
                        (Original: {request.previousTimeIn})
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <FieldRow label="Reason" value={request.reason} />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <FieldRow label="Current Responsibility" value={request.currentResponsibility} />
          </div>
          <FieldRow label="Hand Over To" value={request.handoverTo} />
        </div>
      );
    }

    if (t === 'extra_work') return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        <FieldRow label="Work Date" value={formatDate(request.workDate)} />
        <FieldRow label="Work Type" value={request.workType?.replace(/_/g, ' ')} />
        <FieldRow label="Start Time" value={request.startTime} />
        <FieldRow label="End Time" value={request.endTime} />
        <FieldRow label="Total Hours" value={`${request.totalHours} hours`} />
        <FieldRow label="Project / Client" value={request.projectClient} />
        <div style={{ gridColumn: '1/-1' }}>
          <FieldRow label="Work Description" value={request.description} />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <FieldRow label="Output Achieved" value={request.outputAchieved} />
        </div>

      </div>
    );
  };

  if (loading) return (
    <div className="d-flex wrms-page">
      <Sidebar />
      <div className="flex-grow-1">
        <Navbar />
        <div className="wrms-container" style={{ textAlign: 'center', padding: '80px' }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: '16px', color: '#64748b' }}>Loading request details...</p>
        </div>
      </div>
    </div>
  );

  if (!request) return (
    <div className="d-flex wrms-page">
      <Sidebar />
      <div className="flex-grow-1">
        <Navbar />
        <div className="wrms-container">
          <div className="wrms-empty-state">
            <div className="wrms-empty-state-icon"><FaQuestionCircle size={36} color="#94a3b8" /></div>
            <h5>Request Not Found</h5>
            <p>This request may have been deleted or you may not have access.</p>
            <button className="wrms-btn-primary" onClick={() => navigate('/wrms')} style={{ marginTop: '16px' }}>
              Back to Requests
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="d-flex wrms-page">
      <Sidebar />
      <div className="flex-grow-1">
        <Navbar />
        <div className="wrms-container">
          {/* Back + Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            <button onClick={() => navigate(-1)}
              style={{ background: 'white', border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, fontSize: '0.82rem', color: '#64748b' }}>
              <FaArrowLeft size={12} /> Back
            </button>
            <div>
              <div className="wrms-breadcrumb">Requests · {getRequestTypeLabel(type)} · <span>{request.requestId}</span></div>
              <h1 className="wrms-page-title" style={{ fontSize: '1.4rem' }}>{getRequestTypeLabel(type)} Detail</h1>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <StatusBadge status={request.status} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px' }}>
            {/* Main: Form Details */}
            <div>
              <div className="wrms-card" style={{ overflow: 'hidden', marginBottom: '24px' }}>
                <WRMSFormHeader
                  formTitle={getRequestTypeLabel(type)}
                  formSubtitle="Digi Sahyadri — Request System"
                  requestId={request.requestId}
                />
                <div style={{ padding: '28px' }}>
                  {/* Employee Info */}
                  <div className="wrms-section-label">Employee Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', marginBottom: '24px' }}>
                    <FieldRow label="Name" value={request.employeeName} />
                    <FieldRow label="Team ID" value={request.teamId} />
                    <FieldRow label="Designation" value={request.designation} />
                    <FieldRow label="Department" value={request.department} />
                  </div>

                  <div className="wrms-section-label" style={{ marginTop: '8px' }}>Request Details</div>
                  {renderFormFields()}

                  {/* Signature Footer */}
                  <SignatureFooter signatories={buildSignatories()} />
                </div>
              </div>

              {/* Approval Log (Immutable Audit Trail) */}
              {logs.length > 0 && (
                <div className="wrms-card">
                  <div className="wrms-card-header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaHistory style={{ color: '#4f46e5' }} />
                    <h6 style={{ fontWeight: 700, margin: 0, color: '#0f172a' }}>Approval Audit Trail (Immutable)</h6>
                  </div>
                  <div className="wrms-card-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {logs.map(log => {
                        let borderColor = '#4f46e5';
                        if (log.action === 'approved' || log.action === 'manual_add') borderColor = '#059669';
                        else if (log.action === 'rejected') borderColor = '#dc2626';
                        else if (log.action === 'cancelled') borderColor = '#64748b';
                        else if (log.action === 'time_in_updated') borderColor = '#d97706';

                        return (
                          <div key={log.id} style={{
                            background: '#f8fafc', border: '1px solid #e2e8f0',
                            borderRadius: '10px', padding: '12px 16px',
                            borderLeft: `4px solid ${borderColor}`,
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0f172a', textTransform: 'capitalize' }}>
                                {log.action === 'time_in_updated' ? 'Time In Edited' : log.action?.replace(/_/g, ' ')} by {log.approverRole?.replace(/_/g, ' ')}
                              </span>
                              <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{formatDateTime(log.actedAt)}</span>
                            </div>
                            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                              <strong>{log.approverName}</strong> — {log.remarks}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Timeline + Actions */}
            <div>
              <div className="wrms-card" style={{ marginBottom: '16px' }}>
                <div className="wrms-card-header">
                  <h6 style={{ fontWeight: 700, margin: 0, color: '#0f172a' }}>Approval Progress</h6>
                </div>
                <div className="wrms-card-body">
                  <ApprovalTimeline
                    chain={chain}
                    currentStage={request.currentStage}
                    status={request.status}
                    approvalLogs={logs}
                    request={request}
                  />
                </div>
              </div>

              {/* Approver / Admin Action Buttons */}
              {(canApprove || canOverride) && request.status === 'pending' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                  {request.currentStage === 'handover' && (
                    <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '10px', padding: '10px 14px', fontSize: '0.78rem', color: '#3730a3' }}>
                      <strong>Handover Stage:</strong> Review the work responsibility details and accept handover. Once approved, this request will automatically proceed to the Reporting Manager.
                    </div>
                  )}
                  <button className="wrms-btn-primary" onClick={handleApprove} disabled={processing}
                    style={{ width: '100%', justifyContent: 'center', padding: '12px' }}>
                    <FaCheck size={13} />
                    {processing
                      ? 'Processing...'
                      : request.currentStage === 'handover'
                      ? 'Accept & Approve Handover'
                      : 'Approve This Request'}
                  </button>
                  {isElevatedRole && (
                    <button
                      className="wrms-btn-warning"
                      onClick={() => { setHoldModal(true); setHoldRemarks(''); }}
                      disabled={processing}
                      style={{
                        width: '100%',
                        justifyContent: 'center',
                        padding: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        borderRadius: '10px',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        background: '#fef3c7',
                        color: '#b45309',
                        border: '1px solid #fde68a',
                      }}>
                      <FaClock size={13} /> Put On Hold
                    </button>
                  )}
                  <button className="wrms-btn-danger"
                    onClick={() => { setRejectModal(true); setRemarks(''); }}
                    disabled={processing}
                    style={{ width: '100%', justifyContent: 'center', padding: '12px', display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '10px', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}>
                    <FaTimes size={13} /> {request.currentStage === 'handover' ? 'Decline Handover' : 'Reject This Request'}
                  </button>
                </div>
              )}

              {/* On-Hold State Actions (Re-Approve or Reject) */}
              {(canApprove || isElevatedRole) && request.status === 'on_hold' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '14px' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#b45309', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaClock size={12} /> Request Currently On Hold
                  </div>
                  <button className="wrms-btn-primary" onClick={handleApprove} disabled={processing}
                    style={{ width: '100%', justifyContent: 'center', padding: '11px' }}>
                    <FaCheck size={13} />
                    {processing ? 'Processing...' : 'Re-Approve Request'}
                  </button>
                  <button className="wrms-btn-danger"
                    onClick={() => { setRejectModal(true); setRemarks(''); }}
                    disabled={processing}
                    style={{ width: '100%', justifyContent: 'center', padding: '11px', display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '10px', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}>
                    <FaTimes size={13} /> Reject Request
                  </button>
                </div>
              )}

              {/* Management Override for Approved Requests (HR / CEO / Founder / Admin can Hold or Reject ONLY on or before From Date) */}
              {isElevatedRole && request.status === 'approved' && (
                canActionApproved ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#475569' }}>
                      Management Action (HR / Admin / CEO)
                    </div>
                    <button
                      className="wrms-btn-warning"
                      onClick={() => { setHoldModal(true); setHoldRemarks(''); }}
                      disabled={processing}
                      style={{
                        width: '100%',
                        justifyContent: 'center',
                        padding: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        borderRadius: '10px',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        background: '#fef3c7',
                        color: '#b45309',
                        border: '1px solid #fde68a',
                      }}>
                      <FaClock size={13} /> Put Approved Leave On Hold
                    </button>
                    <button
                      className="wrms-btn-danger"
                      onClick={() => { setRejectModal(true); setRemarks(''); }}
                      disabled={processing}
                      style={{
                        width: '100%',
                        justifyContent: 'center',
                        padding: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        borderRadius: '10px',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        cursor: 'pointer'
                      }}>
                      <FaTimes size={13} /> Reject Approved Leave
                    </button>
                    <div style={{ fontSize: '0.68rem', color: '#64748b', textAlign: 'center' }}>
                      Will automatically refund deducted days to balance
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 14px', fontSize: '0.78rem', color: '#64748b' }}>
                    <FaClock size={12} style={{ color: '#94a3b8' }} />
                    <span>Action window closed: Approved leave cannot be put on hold or rejected after its From Date.</span>
                  </div>
                )
              )}

              {/* Status Banners (for Non-Pending or Cancelled) */}
              {request.status !== 'pending' && (
                <div style={{
                  background: request.status === 'approved' ? '#ecfdf5' : request.status === 'cancelled' ? '#f8fafc' : request.status === 'on_hold' ? '#fffbeb' : '#fef2f2',
                  border: `1px solid ${request.status === 'approved' ? '#a7f3d0' : request.status === 'cancelled' ? '#cbd5e1' : request.status === 'on_hold' ? '#fde68a' : '#fca5a5'}`,
                  borderRadius: '12px',
                  padding: '16px',
                  textAlign: 'center',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>
                    {request.status === 'approved' ? (
                      <FaCheckCircle size={28} color="#059669" />
                    ) : request.status === 'cancelled' ? (
                      <FaBan size={28} color="#64748b" />
                    ) : request.status === 'on_hold' ? (
                      <FaClock size={28} color="#d97706" />
                    ) : (
                      <FaTimesCircle size={28} color="#dc2626" />
                    )}
                  </div>
                  <div style={{
                    fontWeight: 700,
                    color: request.status === 'approved' ? '#065f46' : request.status === 'cancelled' ? '#475569' : request.status === 'on_hold' ? '#92400e' : '#991b1b',
                    fontSize: '0.875rem'
                  }}>
                    Request {request.status === 'approved' ? 'Approved' : request.status === 'cancelled' ? 'Cancelled' : request.status === 'on_hold' ? 'On Hold' : 'Rejected'}
                  </div>
                  {request.status === 'approved' && request.applicationApprovedOn && (
                    <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                      On {formatDate(request.applicationApprovedOn)}
                    </div>
                  )}
                  {request.status === 'on_hold' && (
                    <>
                      {request.onHoldAt && (
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                          Put on hold on {formatDateTime(request.onHoldAt)}
                        </div>
                      )}
                      {request.holdRemarks && (
                        <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '8px', background: '#fef3c7', padding: '6px 10px', borderRadius: '6px', textAlign: 'left', wordBreak: 'break-word' }}>
                          <strong>Hold Remarks:</strong> {request.holdRemarks}
                        </div>
                      )}
                    </>
                  )}
                  {request.status === 'cancelled' && (
                    <>
                      {request.cancelledAt && (
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                          Cancelled on {formatDateTime(request.cancelledAt)}
                        </div>
                      )}
                      {request.cancellationReason && (
                        <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '8px', background: '#f1f5f9', padding: '6px 10px', borderRadius: '6px', textAlign: 'left', wordBreak: 'break-word' }}>
                          <strong>Reason:</strong> {request.cancellationReason}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Gate Pass Edit Time In Action (Applicant or Admin/HR on or before gate pass date) */}
              {canEditGatePassTimeIn && (
                <div style={{ marginBottom: '16px' }}>
                  <button
                    className="wrms-btn-primary"
                    onClick={() => {
                      setTimeInInput(request.expectedTimeIn || request.timeIn || '');
                      setTimeInModal(true);
                    }}
                    disabled={processing}
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                      padding: '11px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '10px',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      background: '#2563eb',
                      borderColor: '#2563eb',
                    }}
                  >
                    <FaEdit size={13} /> Edit Return Time (Time In)
                  </button>
                  <div style={{ fontSize: '0.68rem', color: '#64748b', textAlign: 'center', marginTop: '6px' }}>
                    Only Time In can be edited on or before the gate pass date
                  </div>
                </div>
              )}

              {/* Employee-Only Cancel Button (Available for Pending or Approved requests before/on the leave date) */}
              {canCancel && (
                <div style={{ marginTop: '8px' }}>
                  <button
                    className="wrms-btn-outline"
                    onClick={() => { setCancelModal(true); setCancelReason(''); }}
                    disabled={processing}
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '10px',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      color: '#dc2626',
                      borderColor: '#fca5a5',
                      background: '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    title="Only you (the applicant) can cancel this request"
                  >
                    <FaBan size={13} /> Cancel My Request
                  </button>
                  <div style={{ fontSize: '0.68rem', color: '#94a3b8', textAlign: 'center', marginTop: '6px' }}>
                    Available on or before the leave date
                  </div>
                </div>
              )}

              {isPastDate && (
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontSize: '0.75rem',
                  color: '#64748b',
                  textAlign: 'center',
                  marginTop: '8px'
                }}>
                  <strong>Cancellation window closed:</strong> Requests can only be cancelled on or before the leave date.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      <Modal show={rejectModal} onHide={() => setRejectModal(false)} centered className="wrms-approve-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: 800, fontSize: '1rem', color: '#dc2626' }}>
            {request.status === 'approved' ? 'Reject Approved Request' : 'Reject Request'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          {request.status === 'approved' && request.requestType === 'leave' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.82rem', color: '#991b1b' }}>
              <strong>Notice:</strong> This leave was previously <strong>Approved</strong>. Rejecting it will revoke the approval and automatically refund <strong>{request.daysCount} {request.daysCount === 1 ? 'day' : 'days'}</strong> back to the employee's leave balance.
            </div>
          )}
          <Form.Group>
            <Form.Label style={{ fontWeight: 700, fontSize: '0.82rem' }}>
              Rejection Reason <span style={{ color: '#dc2626' }}>*</span>
            </Form.Label>
            <Form.Control as="textarea" rows={3}
              placeholder="Please provide a clear reason. The employee will be notified."
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              style={{ borderRadius: '10px' }} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f1f5f9', padding: '16px 24px' }}>
          <button className="wrms-btn-outline" onClick={() => setRejectModal(false)} style={{ padding: '8px 20px' }}>Cancel</button>
          <button onClick={handleRejectConfirm} disabled={processing}
            style={{ padding: '8px 24px', borderRadius: '8px', border: 'none', fontWeight: 700, background: '#dc2626', color: 'white', cursor: 'pointer' }}>
            {processing ? 'Rejecting...' : 'Confirm Rejection'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Hold Modal (HR / CEO / Founder / Admin) */}
      <Modal show={holdModal} onHide={() => setHoldModal(false)} centered className="wrms-approve-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: 800, fontSize: '1rem', color: '#b45309' }}>
            Put Request On Hold
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          <p style={{ fontSize: '0.88rem', color: '#334155', marginBottom: '16px' }}>
            Are you sure you want to put <strong>{getRequestTypeLabel(type)}</strong> ({request.requestId}) on hold?
          </p>
          {request.status === 'approved' && request.requestType === 'leave' && (
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.82rem', color: '#92400e' }}>
              <strong>Leave Balance Restoration:</strong> Putting this approved leave on hold will temporarily restore <strong>{request.daysCount} {request.daysCount === 1 ? 'day' : 'days'}</strong> back to the employee's balance until it is re-approved.
            </div>
          )}
          <Form.Group>
            <Form.Label style={{ fontWeight: 700, fontSize: '0.82rem' }}>
              Reason / Remarks for Hold <span style={{ color: '#dc2626' }}>*</span>
            </Form.Label>
            <Form.Control as="textarea" rows={3}
              placeholder="Please provide a clear reason for putting this request on hold..."
              value={holdRemarks}
              onChange={e => setHoldRemarks(e.target.value)}
              style={{ borderRadius: '10px', fontSize: '0.85rem' }} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f1f5f9', padding: '16px 24px' }}>
          <button className="wrms-btn-outline" onClick={() => setHoldModal(false)} style={{ padding: '8px 20px' }}>Cancel</button>
          <button onClick={handleHoldConfirm} disabled={processing}
            style={{ padding: '8px 24px', borderRadius: '8px', border: 'none', fontWeight: 700, background: '#d97706', color: 'white', cursor: 'pointer' }}>
            {processing ? 'Processing...' : 'Confirm Hold'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Modal (Employee Only) */}
      <Modal show={cancelModal} onHide={() => setCancelModal(false)} centered className="wrms-approve-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: 800, fontSize: '1rem', color: '#dc2626' }}>
            Cancel Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          <p style={{ fontSize: '0.88rem', color: '#334155', marginBottom: '16px' }}>
            Are you sure you want to cancel your <strong>{getRequestTypeLabel(type)}</strong> ({request.requestId})?
          </p>
          {request.status === 'approved' && request.requestType === 'leave' && (
            <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.82rem', color: '#065f46' }}>
              <strong>Leave Balance Restore:</strong> Since this leave was approved, cancelling it will automatically restore <strong>{request.daysCount} {request.daysCount === 1 ? 'day' : 'days'}</strong> back to your leave balance.
            </div>
          )}
          <Form.Group>
            <Form.Label style={{ fontWeight: 700, fontSize: '0.82rem' }}>
              Reason for Cancellation (Optional)
            </Form.Label>
            <Form.Control as="textarea" rows={3}
              placeholder="e.g. Plans changed / Leave not taken"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              style={{ borderRadius: '10px', fontSize: '0.85rem' }} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f1f5f9', padding: '16px 24px' }}>
          <button className="wrms-btn-outline" onClick={() => setCancelModal(false)} style={{ padding: '8px 20px' }}>
            No, Keep Request
          </button>
          <button onClick={handleCancelConfirm} disabled={processing}
            style={{ padding: '8px 24px', borderRadius: '8px', border: 'none', fontWeight: 700, background: '#dc2626', color: 'white', cursor: 'pointer' }}>
            {processing ? 'Cancelling...' : 'Yes, Cancel Request'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Edit Gate Pass Time In Modal */}
      <Modal show={timeInModal} onHide={() => !updatingTimeIn && setTimeInModal(false)} centered className="wrms-approve-modal">
        <Modal.Header closeButton={!updatingTimeIn}>
          <Modal.Title style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaClock style={{ color: '#2563eb' }} /> Edit Gate Pass Time In
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 16px', marginBottom: '18px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.8rem' }}>
              <div>
                <span style={{ color: '#64748b' }}>Gate Pass Date:</span>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{formatDate(request?.date || request?.returnDate)}</div>
              </div>
              <div>
                <span style={{ color: '#64748b' }}>Time Out:</span>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{request?.timeOut || '—'}</div>
              </div>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '8px', borderTop: '1px dashed #cbd5e1', paddingTop: '6px' }}>
              🔒 <strong>Locked Fields:</strong> Date, Reason, and Time Out cannot be modified. Only <strong>Time In</strong> can be updated on or before this gate pass date.
            </div>
          </div>

          <Form.Group>
            <Form.Label style={{ fontWeight: 700, fontSize: '0.82rem' }}>
              Time In / Return Time <span style={{ color: '#dc2626' }}>*</span>
            </Form.Label>
            <Form.Control
              type="time"
              value={timeInInput}
              onChange={e => setTimeInInput(e.target.value)}
              style={{ fontSize: '1rem', fontWeight: 600, padding: '10px', borderRadius: '8px' }}
              required
            />
            <Form.Text style={{ fontSize: '0.72rem', color: '#64748b' }}>
              Enter your updated or actual return time.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f1f5f9', padding: '14px 24px' }}>
          <button
            className="wrms-btn-outline"
            onClick={() => setTimeInModal(false)}
            disabled={updatingTimeIn}
            style={{ padding: '8px 18px', fontSize: '0.82rem' }}
          >
            Cancel
          </button>
          <button
            className="wrms-btn-primary"
            onClick={handleSaveTimeIn}
            disabled={updatingTimeIn || !timeInInput}
            style={{ padding: '8px 22px', fontSize: '0.82rem', background: '#2563eb', borderColor: '#2563eb' }}
          >
            {updatingTimeIn ? 'Saving...' : 'Save Time In'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RequestDetailPage;
